"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _react2 = require("@floating-ui/react");
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _useTrigger = _interopRequireDefault(require("../../hooks/useTrigger"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/**
 * The Tooltip component provides a way to display additional information when an user hover another UI element.
 *
 * This component is an opinionated wrapper around <a href="https://github.com/floating-ui/floating-ui">Floating-UI</a>.
 */
const Tooltip = props => {
  const {
    trigger: initialTrigger,
    id: controlledId,
    position,
    strategy,
    width,
    className,
    children,
    ...attr
  } = props;
  const id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  const {
    trigger
  } = (0, _useTrigger.default)(initialTrigger, {
    ...initialTrigger.props,
    className: initialTrigger.props && initialTrigger.props.className
  });
  const [isOpen, setIsOpen] = _react.default.useState(false);
  const arrowRef = _react.default.useRef(null);
  const {
    refs,
    floatingStyles,
    context
  } = (0, _react2.useFloating)({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: position,
    strategy: strategy,
    // Make sure the tooltip stays on the screen
    whileElementsMounted: _react2.autoUpdate,
    middleware: [(0, _react2.offset)(8), (0, _react2.flip)({
      fallbackAxisSideDirection: "start"
    }), (0, _react2.shift)(), (0, _react2.arrow)({
      element: arrowRef
    })]
  });

  // Event listeners to change the open state
  const hover = (0, _react2.useHover)(context, {
    move: false
  });
  const focus = (0, _react2.useFocus)(context);
  const dismiss = (0, _react2.useDismiss)(context);
  // Role props for screen readers
  const role = (0, _react2.useRole)(context, {
    role: "tooltip"
  });

  // Merge all the interactions into prop getters
  const {
    getReferenceProps,
    getFloatingProps
  } = (0, _react2.useInteractions)([hover, focus, dismiss, role]);
  return /*#__PURE__*/_react.default.createElement("div", _extends({
    className: (0, _clsx.default)('uk-tooltip', className),
    id: id
  }, attr), /*#__PURE__*/_react.default.createElement("div", _extends({
    className: "uk-tooltip__trigger",
    ref: refs.setReference
  }, getReferenceProps()), trigger), isOpen && /*#__PURE__*/_react.default.createElement(_react2.FloatingPortal, {
    id: id
  }, /*#__PURE__*/_react.default.createElement("div", _extends({
    className: "uk-tooltip__content",
    ref: refs.setFloating,
    style: width ? {
      ...floatingStyles,
      minWidth: width
    } : floatingStyles
  }, getFloatingProps()), /*#__PURE__*/_react.default.createElement(_react2.FloatingArrow, {
    ref: arrowRef,
    context: context
  }), children)));
};
Tooltip.propTypes = {
  /**
   * ID used to meet accessibility standards (WAI-ARIA Tooltip Pattern)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,
  /**
   * Trigger of the Tooltip
   * If the trigger isn't a React element, it will be wrapped within a <span/>
   */
  trigger: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]).isRequired,
  /**
   * Position of the tooltip relative to its trigger
   */
  position: _propTypes.default.oneOf(['top', 'bottom', 'left', 'right']),
  /**
   * Change width prop
   */
  width: _propTypes.default.string,
  strategy: _propTypes.default.oneOf(['absolute', 'fixed'])
};
Tooltip.defaultProps = {
  trigger: '',
  position: 'top',
  width: 'auto',
  strategy: 'fixed'
};
var _default = exports.default = Tooltip;